import { CalendarById } from "@/types/calendar";
import { createContext, ReactNode } from "react";

// types
import {
  BookingType,
  BookingContextType,
  bookingInfo,
  Info,
  Payment,
  Booking,
  ServiceCommon,
  ServiceType,
  Consent,
  SearchParams,
  IntakeFields,
  RequiredDocument,
  AdditionalQuestion,
} from "utils/booking";

import axiosInstance from "@/utils/axios-util";

const BookingContext = createContext<BookingType | BookingContextType>(
  bookingInfo,
);
// ========================================================================
type Props = {
  children: ReactNode;
};

function BookingProvider({ children }: Props) {
  const reset = (data?: BookingType) => {
    localStorage.setItem(
      "thrivelabBooking",
      data ? JSON.stringify(data) : JSON.stringify(bookingInfo),
    );
  };

  const handleSetData = (data: BookingType) => {
    const now = new Date();
    localStorage.setItem(
      "thrivelabBooking",
      JSON.stringify({ ...data, expiry: now.getTime() + 3600 }),
    );
    getData();
  };

  const getData = (): BookingType => {
    let booking = bookingInfo;
    try {
      const store = localStorage.getItem("thrivelabBooking");
      if (store) {
        booking = JSON.parse(store) as BookingType;
      } else {
        localStorage.setItem("thrivelabBooking", JSON.stringify(bookingInfo));
      }
      // eslint-disable-next-line no-empty
    } catch (error) {}
    return booking;
  };

  const handleSetInfo = (payload: Info) => {
    // eslint-disable-next-line no-unused-vars
    const { info, ...rest } = getData().data;
    handleSetData({ data: { info: payload, ...rest } });
  };

  const handleSetPayment = (payload: Payment) => {
    // eslint-disable-next-line no-unused-vars
    const { payment, ...rest } = getData().data;
    handleSetData({ data: { payment: payload, ...rest } });
  };

  const handleSetConsent = (payload: Consent) => {
    // eslint-disable-next-line no-unused-vars
    const { consent, ...rest } = getData().data;
    handleSetData({ data: { consent: payload, ...rest } });
  };

  const handleSetBooking = (payload: Booking) => {
    // eslint-disable-next-line no-unused-vars
    const { booking, ...rest } = getData().data;
    handleSetData({ data: { booking: payload, ...rest } });
  };

  const handleSetFreeConsult = (payload: ServiceCommon) => {
    // eslint-disable-next-line no-unused-vars
    const { freeConsult, ...rest } = getData().data;
    handleSetData({ data: { freeConsult: payload, ...rest } });
  };

  const handleSetParams = (payload: SearchParams) => {
    // eslint-disable-next-line no-unused-vars
    const { search, ...rest } = getData().data;
    handleSetData({ data: { search: payload, ...rest } });
  };
  const handleSetMeetingDetails = (payload: CalendarById | null) => {
    // eslint-disable-next-line no-unused-vars
    const { meetingDetails, ...rest } = getData().data;
    handleSetData({ data: { meetingDetails: payload, ...rest } });
  };
  const handleSetIntake = (payload: IntakeFields) => {
    // eslint-disable-next-line no-unused-vars
    const { intake, ...rest } = getData().data;
    if (payload.user?.accessToken)
      axiosInstance.defaults.headers.common.Authorization = `Bearer ${payload.user?.accessToken}`;

    handleSetData({ data: { intake: payload, ...rest } });
  };

  const handleSetAdditionalQuestions = (
    payload: AdditionalQuestion[][] | null,
  ) => {
    // eslint-disable-next-line no-unused-vars
    const { additionalQuestions, ...rest } = getData().data;
    handleSetData({ data: { additionalQuestions: payload, ...rest } });
  };

  const handleSetRequiredDocs = (payload: RequiredDocument[][] | null) => {
    // eslint-disable-next-line no-unused-vars
    const { requiredDocuments, ...rest } = getData().data;
    handleSetData({ data: { requiredDocuments: payload, ...rest } });
  };

  const handleSetServices = (payload: ServiceType[] | null) => {
    // eslint-disable-next-line no-unused-vars
    const { services, ...rest } = getData().data;
    handleSetData({ data: { services: payload, ...rest } });
  };
  const handleSetPayors = (payload: string[]) => {
    // eslint-disable-next-line no-unused-vars
    const { payors, ...rest } = getData().data;
    handleSetData({ data: { payors: payload ?? [], ...rest } });
  };

  return (
    <BookingContext.Provider
      value={{
        data: getData().data,
        setInfo: handleSetInfo,
        setPayment: handleSetPayment,
        setConsent: handleSetConsent,
        setBooking: handleSetBooking,
        setParams: handleSetParams,
        setIntake: handleSetIntake,
        setFreeConsult: handleSetFreeConsult,
        setAdditionalQuestions: handleSetAdditionalQuestions,
        setRequiredDocs: handleSetRequiredDocs,
        setMeetingDetail: handleSetMeetingDetails,
        setServices: handleSetServices,
        setPayors: handleSetPayors,
        reset: reset,
      }}
    >
      {children}
    </BookingContext.Provider>
  );
}

export { BookingContext, BookingProvider };
